import * as React from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Settings2 } from 'lucide-react';
import type { LeaveStatus, LeaveTypeSummary } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Select } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const peopleLeaveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/people/leave',
  component: PeopleLeavePage,
});

const STATUS_LABEL: Record<LeaveStatus, string> = {
  requested: 'Requested',
  approved: 'Approved',
  rejected: 'Rejected',
  availed: 'Availed',
  cancelled: 'Cancelled',
};

const STATUS_VARIANT: Record<LeaveStatus, 'default' | 'muted' | 'destructive'> = {
  requested: 'muted',
  approved: 'default',
  rejected: 'destructive',
  availed: 'default',
  cancelled: 'muted',
};

function formatDaysX10(x10: number): string {
  const whole = Math.trunc(x10 / 10);
  const frac = Math.abs(x10 % 10);
  return frac === 0 ? String(whole) : `${whole}.${frac}`;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function inclusiveDaysX10(fromDate: string, toDate: string): number {
  const from = new Date(`${fromDate}T00:00:00.000Z`).getTime();
  const to = new Date(`${toDate}T00:00:00.000Z`).getTime();
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return 0;
  return (Math.round((to - from) / (24 * 60 * 60 * 1000)) + 1) * 10;
}

const LEAVE_TYPE_MANAGER_ROLES = ['Admin', 'Finance', 'HR'];

function leaveTypeOptionLabel(t: LeaveTypeSummary): string {
  const quota =
    t.quotaX10 === 0
      ? null
      : `${formatDaysX10(t.quotaX10)} days/${t.quotaPeriod === 'month' ? 'month' : 'yr'}`;
  const detail = t.paid ? quota : 'unpaid';
  return detail ? `${t.name} · ${detail}` : t.name;
}

function PeopleLeavePage(): JSX.Element {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = React.useState<LeaveStatus | ''>('');
  const [error, setError] = React.useState<string | null>(null);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [personId, setPersonId] = React.useState('');
  const [leaveTypeId, setLeaveTypeId] = React.useState('');
  const [fromDate, setFromDate] = React.useState(todayIso());
  const [toDate, setToDate] = React.useState(todayIso());
  const [halfDay, setHalfDay] = React.useState(false);
  const [reason, setReason] = React.useState('');

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false });
  const peopleQuery = useQuery({
    queryKey: ['people-simple'],
    queryFn: () => api.listPeopleSimple(),
  });
  const typesQuery = useQuery({ queryKey: ['leave-types'], queryFn: () => api.listLeaveTypes() });

  const listQuery = useQuery({
    queryKey: ['leave-requests', statusFilter],
    queryFn: () =>
      api.listLeaveRequests(statusFilter === '' ? {} : { status: statusFilter as LeaveStatus }),
  });

  React.useEffect(() => {
    if (meQuery.data && personId === '') setPersonId(meQuery.data.person.id);
  }, [meQuery.data, personId]);
  const types = (typesQuery.data ?? []).filter((t) => t.active);
  const canManageTypes = (meQuery.data?.person.roles ?? []).some((r) =>
    LEAVE_TYPE_MANAGER_ROLES.includes(r),
  );
  const selectedType = types.find((t) => t.id === leaveTypeId) ?? null;
  const balanceQuery = useQuery({
    queryKey: ['leave-balance', personId, leaveTypeId, fromDate],
    queryFn: () => api.getLeaveBalance(personId, leaveTypeId, fromDate),
    enabled:
      createOpen && personId !== '' && leaveTypeId !== '' && /^\d{4}-\d{2}-\d{2}$/.test(fromDate),
  });
  const balance = balanceQuery.data ?? null;
  const requestedX10 = halfDay ? 5 : inclusiveDaysX10(fromDate, toDate);
  const overQuota =
    balance !== null && balance.remainingX10 !== null && requestedX10 > balance.remainingX10;

  const createMutation = useMutation({
    mutationFn: async () => {
      return api.createLeaveRequest({
        personId,
        leaveTypeId,
        fromDate,
        toDate,
        halfDay,
        reason: reason.trim() === '' ? undefined : reason.trim(),
      });
    },
    onSuccess: async () => {
      setCreateOpen(false);
      setReason('');
      setHalfDay(false);
      setLeaveTypeId('');
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leave-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['leave-balance'] }),
      ]);
    },
    onError: (err) => setError(toMessage(err)),
  });

  const approve = useMutation({
    mutationFn: (id: string) => api.approveLeaveRequest(id),
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leave-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['leave-balance'] }),
      ]);
    },
    onError: (err) => setError(toMessage(err)),
  });

  const reject = useMutation({
    mutationFn: (id: string) => api.rejectLeaveRequest(id, { note: 'rejected via UI' }),
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leave-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['leave-balance'] }),
      ]);
    },
    onError: (err) => setError(toMessage(err)),
  });

  const avail = useMutation({
    mutationFn: (id: string) => api.availLeaveRequest(id),
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leave-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['leave-balance'] }),
      ]);
    },
    onError: (err) => setError(toMessage(err)),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelLeaveRequest(id, { reason: 'cancelled via UI' }),
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leave-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['leave-balance'] }),
      ]);
    },
    onError: (err) => setError(toMessage(err)),
  });

  const rows = listQuery.data ?? [];
  const people = peopleQuery.data ?? [];

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Leave requests
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            File a request for yourself or an employee, or action pending requests.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>New request</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New leave request</DialogTitle>
              <DialogDescription>
                Non-overlapping with existing pending/approved leave. Dates in closed months are
                blocked.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Employee</Label>
                <Select value={personId} onChange={(e) => setPersonId(e.currentTarget.value)}>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.email})
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="leave-type">Leave type</Label>
                  {canManageTypes ? (
                    <Link
                      to="/people/holidays"
                      className="inline-flex items-center gap-1 text-xs text-[hsl(var(--brand))] hover:underline"
                    >
                      <Settings2 className="size-3" />
                      Manage leave types
                    </Link>
                  ) : null}
                </div>
                <Select
                  id="leave-type"
                  value={leaveTypeId}
                  onChange={(e) => setLeaveTypeId(e.currentTarget.value)}
                  disabled={typesQuery.isPending}
                >
                  <option value="">
                    {typesQuery.isPending
                      ? 'Loading…'
                      : types.length === 0
                        ? 'No leave types configured'
                        : 'Select a leave type…'}
                  </option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {leaveTypeOptionLabel(t)}
                    </option>
                  ))}
                </Select>
                {balance && balance.remainingX10 !== null && balance.windowLabel ? (
                  <p
                    className={cn(
                      'text-xs',
                      overQuota
                        ? 'text-[hsl(var(--destructive))]'
                        : 'text-[hsl(var(--muted-foreground))]',
                    )}
                  >
                    {formatDaysX10(balance.remainingX10)} of {formatDaysX10(balance.quotaX10)} days
                    left for {balance.windowLabel}
                    {overQuota ? ` — this request needs ${formatDaysX10(requestedX10)}.` : '.'}
                  </p>
                ) : null}
                {selectedType && !selectedType.paid ? (
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Unpaid: these days reduce paid days for the month.
                  </p>
                ) : null}
                {!typesQuery.isPending && types.length === 0 && canManageTypes ? (
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Add leave types under People · Leave types & holidays before filing a request.
                  </p>
                ) : null}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label>From</Label>
                  <Input
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.currentTarget.value);
                      if (toDate < e.currentTarget.value) setToDate(e.currentTarget.value);
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>To</Label>
                  <Input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.currentTarget.value)}
                    min={fromDate}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={halfDay}
                  disabled={fromDate !== toDate}
                  onChange={(e) => setHalfDay(e.currentTarget.checked)}
                />
                Half-day (single day only)
              </label>
              <div className="flex flex-col gap-1.5">
                <Label>Reason (optional)</Label>
                <Input value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
              </div>
              {error ? (
                <Alert variant="destructive">
                  <AlertTitle>Could not create</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </div>
            <DialogFooter>
              <Button
                onClick={() => createMutation.mutate()}
                disabled={
                  personId === '' ||
                  leaveTypeId === '' ||
                  createMutation.isPending ||
                  toDate < fromDate
                }
              >
                {createMutation.isPending ? 'Submitting…' : 'Submit request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {error && !createOpen ? (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4">
          <div>
            <CardTitle>Requests</CardTitle>
            <CardDescription>{rows.length} shown</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="status" className="text-xs">
              Filter
            </Label>
            <Select
              id="status"
              className="h-9 w-40"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.currentTarget.value as LeaveStatus | '')}
            >
              <option value="">All</option>
              <option value="requested">Requested</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="availed">Availed</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              No requests match this filter.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {rows.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-col gap-2 py-3 text-sm md:flex-row md:items-center md:justify-between"
                >
                  <div className="flex flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{r.personName ?? r.personId}</span>
                      <Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                      <Badge variant={r.paid ? 'default' : 'destructive'}>
                        {r.paid ? 'Paid' : 'Unpaid'}
                      </Badge>
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {r.leaveTypeName} · {r.fromDate} → {r.toDate}
                      {r.halfDay ? ' · half-day' : ''} · {formatDaysX10(r.daysX10)}d
                    </div>
                    {r.reason ? (
                      <div className="text-xs italic text-[hsl(var(--muted-foreground))]">
                        “{r.reason}”
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {r.status === 'requested' ? (
                      <>
                        <Button size="sm" onClick={() => approve.mutate(r.id)}>
                          Approve
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => reject.mutate(r.id)}>
                          Reject
                        </Button>
                      </>
                    ) : null}
                    {r.status === 'approved' ? (
                      <Button size="sm" variant="outline" onClick={() => avail.mutate(r.id)}>
                        Mark availed
                      </Button>
                    ) : null}
                    {r.status === 'requested' || r.status === 'approved' ? (
                      <Button size="sm" variant="ghost" onClick={() => cancel.mutate(r.id)}>
                        Cancel
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
